/**
 * A YouTube player that reports how much of the video was watched.
 *
 * Presentational and analytics-free: it emits a typed `VideoProgressEvent` through
 * an `onProgress` prop and knows nothing about who is listening. That is what lets
 * the member app wire it to the tracker while the admin panel reuses the very same
 * component as an editor preview with no handler at all — and it keeps this package
 * from importing app code, which it must never do.
 *
 * Three platform details that are not optional:
 *
 * - **`playsinline: 1`.** Without it, iOS hands playback to the native fullscreen
 *   player, the JS API stops receiving state changes, and every quartile silently
 *   disappears — while working perfectly in a desktop browser.
 * - **`origin` only on http(s).** Inside the Capacitor shell the page origin is
 *   `capacitor://localhost`, which YouTube's origin validation rejects outright.
 * - **No autoplay.** It is blocked without a gesture anyway, and a blocked autoplay
 *   that still fires `PLAYING` would inflate the start count.
 */

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect, useRef } from "react";

import { RADIUS } from "../theme/tokens";
import {
  loadYouTubeApi,
  type YouTubeApi,
  type YouTubePlayer,
} from "./youtube-api";

export type VideoQuartile = 25 | 50 | 75 | 100;

export interface VideoProgressEvent {
  kind: "start" | "quartile" | "complete";
  /** 25/50/75 for a quartile, 100 for a completion. Absent on `start`. */
  percent?: VideoQuartile;
  positionSeconds: number;
  durationSeconds: number;
  /** How many quartiles were genuinely watched, so a scrub-to-end stays visible. */
  quartilesWatched: number;
}

export interface VideoPosition {
  positionSeconds: number;
  durationSeconds: number;
}

export interface VideoEmbedProps {
  /** The bare eleven-character id, never a URL. */
  youtubeId: string;
  /** Accessible name for the frame. Already localised — this package ships no catalogue. */
  title: string;
  /** Shown when the player script cannot be reached. Already localised. */
  unavailableLabel: string;
  onProgress?: (event: VideoProgressEvent) => void;
  /**
   * Where the playhead is, every few seconds while playing and whenever playback
   * stops — what "Pokračovat" remembers. Read on mount only, like `startSeconds`.
   */
  onPosition?: (position: VideoPosition) => void;
  /** Open the video at this position rather than at the start. Applied on mount. */
  startSeconds?: number;
  testId?: string;
}

const POLL_INTERVAL_MS = 250;
/** How often a playing video reports its position: every twentieth poll, five seconds. */
const POSITION_EVERY_POLLS = 20;
/** A 250 ms tick advances about a quarter-second. More than this is a seek. */
const SEEK_TOLERANCE_S = 1.5;
const QUARTILES: readonly VideoQuartile[] = [25, 50, 75];

export function VideoEmbed({
  youtubeId,
  title,
  unavailableLabel,
  onProgress,
  onPosition,
  startSeconds = 0,
  testId = "article.video",
}: VideoEmbedProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const failedRef = useRef<HTMLParagraphElement | null>(null);
  const onProgressRef = useRef(onProgress);
  const onPositionRef = useRef(onPosition);
  // Read once: the position to open at belongs to the mount, not to every render that
  // follows a save of where the member now is.
  const startRef = useRef(startSeconds);

  useEffect(() => {
    onProgressRef.current = onProgress;
    onPositionRef.current = onPosition;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let player: YouTubePlayer | null = null;
    let timer: ReturnType<typeof setInterval> | undefined;
    let disposed = false;

    // Two sets, because "already handled" and "actually watched" are different
    // questions: a quartile crossed by dragging the scrubber must never fire again,
    // but it must also not count towards how much was watched.
    const handled = new Set<number>();
    const watched = new Set<number>();
    let lastTime = 0;
    let started = false;
    let completed = false;
    let polls = 0;

    const reportPosition = () => {
      if (!player) return;
      const durationSeconds = player.getDuration();
      if (!durationSeconds || durationSeconds <= 0) return;
      onPositionRef.current?.({
        positionSeconds: player.getCurrentTime(),
        durationSeconds,
      });
    };

    const emit = (
      kind: VideoProgressEvent["kind"],
      positionSeconds: number,
      durationSeconds: number,
      percent?: VideoQuartile,
    ) => {
      onProgressRef.current?.({
        kind,
        percent,
        positionSeconds,
        durationSeconds,
        quartilesWatched: watched.size,
      });
    };

    const poll = () => {
      if (!player) return;
      const durationSeconds = player.getDuration();
      // Still resolving: a zero duration would put every quartile at zero seconds.
      if (!durationSeconds || durationSeconds <= 0) return;
      const positionSeconds = player.getCurrentTime();
      const jumped =
        positionSeconds < lastTime ||
        positionSeconds - lastTime > SEEK_TOLERANCE_S;

      for (const percent of QUARTILES) {
        const threshold = (durationSeconds * percent) / 100;
        if (handled.has(percent) || positionSeconds < threshold) continue;
        // Handled either way, so it can never fire later in this mount. Suppressed
        // rather than deferred when it was crossed by dragging the scrubber: that is
        // the difference between "skipped past it" and "about to watch it".
        handled.add(percent);
        if (jumped && lastTime < threshold) continue;
        watched.add(percent);
        emit("quartile", positionSeconds, durationSeconds, percent);
      }
      lastTime = positionSeconds;
      polls += 1;
      if (polls % POSITION_EVERY_POLLS === 0) reportPosition();
    };

    const stopPolling = () => {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
    };

    const onStateChange = (api: YouTubeApi) => (event: { data?: number }) => {
      if (!player) return;
      if (event.data === api.PlayerState.PLAYING) {
        if (!started) {
          started = true;
          emit("start", player.getCurrentTime(), player.getDuration());
        }
        stopPolling();
        timer = setInterval(poll, POLL_INTERVAL_MS);
        return;
      }

      stopPolling();
      // Any stop — a pause, a seek, the end — is a moment worth remembering.
      reportPosition();
      if (event.data === api.PlayerState.ENDED && !completed) {
        completed = true;
        // Completion comes only from ENDED. `getCurrentTime()` almost never reaches
        // `getDuration()`, so polling for 100% would simply never fire.
        emit("complete", player.getDuration(), player.getDuration(), 100);
      }
    };

    void loadYouTubeApi()
      .then((api) => {
        if (disposed) return;
        player = new api.Player(container, {
          videoId: youtubeId,
          host: "https://www.youtube-nocookie.com",
          playerVars: {
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,
            // Whole seconds: the player ignores a fractional start.
            ...(startRef.current >= 1
              ? { start: Math.floor(startRef.current) }
              : {}),
            ...(window.location.protocol.startsWith("http")
              ? { origin: window.location.origin }
              : {}),
          },
          events: { onStateChange: onStateChange(api) },
        });
      })
      .catch(() => {
        if (disposed || !failedRef.current) return;
        failedRef.current.hidden = false;
        container.hidden = true;
      });

    return () => {
      disposed = true;
      stopPolling();
      player?.destroy();
      player = null;
    };
  }, [youtubeId]);

  return (
    <Box
      data-testid={testId}
      sx={{
        marginBlock: 3,
        borderRadius: `${RADIUS.md}px`,
        overflow: "hidden",
        backgroundColor: "surface.inset",
        // A fixed ratio box, so the article does not reflow when the player loads.
        aspectRatio: "16 / 9",
        position: "relative",
      }}
    >
      <Box
        ref={containerRef}
        title={title}
        sx={{ width: "100%", height: "100%", "& iframe": { border: 0 } }}
      />
      <Typography
        ref={failedRef}
        variant="T3"
        hidden
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          paddingInline: 2,
          textAlign: "center",
          color: "text.secondary",
          // `hidden` is a UA `display: none`, which an author `display: grid` beats.
          // Without this the failure message paints over every working player.
          "&[hidden]": { display: "none" },
        }}
      >
        {unavailableLabel}
      </Typography>
    </Box>
  );
}
